import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, Loader } from 'semantic-ui-react'
import Dialog from '~/components/Dialog'
import { Nullable } from '~/types'
import { FeedPurchaseShowType, ClimaProgramService } from '~/api/services/climaProgram'
import { formatNumber, handleErrors } from '~/utils/helpers'
import {
  FieldsGrid,
  FieldWrapper,
  FieldLabel,
  FieldValue,
  SectionTitle,
  FieldsGridAnimalGroup
} from '../../Registration/style'
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery'
import InfoPopup from '~/components/InfoPopup'
import dayjs from 'dayjs'

interface FeedPurchaseViewModalProps {
  purchaseId: Nullable<string>
  onClose: () => void
}

const FeedPurchaseViewModal: React.FC<FeedPurchaseViewModalProps> = ({ purchaseId, onClose }) => {
  const { t } = useTranslation()

  const [isLoadingFile, setIsLoadingFile] = useState(false)

  const {
    data: purchaseData,
    isLoading: isLoadingPurchaseData,
    refetch: fetchPurchaseData,
    isFetching: isFetchingPurchaseData
  } = ClimaProgramQuery.showPurchase(purchaseId)

  useEffect(() => {
    if (purchaseId) {
      fetchPurchaseData()
    }
  }, [purchaseId])

  function formatDate(date: Date | string | null) {
    if (!date) return '-'
    return dayjs(new Date(date)).format('DD.MM.YYYY')
  }

  function renderField(icon: string, label: string, value: string | number | null | undefined, info?: string) {
    return (
      <FieldWrapper>
        <FieldLabel>
          {info && <InfoPopup
            content={info}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />}
          {icon && <Icon name={icon as any} />}
          {label}
        </FieldLabel>
        <FieldValue>{value ?? '-'}</FieldValue>
      </FieldWrapper>
    )
  }

  async function handleDownloadFile() {
    setIsLoadingFile(true)
    try {
      const response = await ClimaProgramService.getFile(purchaseId)
      const link = document.createElement('a')
      link.href = response.data
      link.download = purchaseData?.purchaseReceiptOriginalFilename || ''
      link.click()
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingFile(false)
  }

  function renderDownloadField(icon: string, label: string, value: string | number | null | undefined) {
    return (
      <FieldWrapper>
        <FieldLabel>
          <Icon name='file' />
          {label}
        </FieldLabel>
        <FieldValue
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => !isLoadingFile && handleDownloadFile()}
        >
          {isLoadingFile ? (
            <Loader active inline size='small' />
          ) : (
            <Icon name={icon as any} />
          )}
          <div>{value}</div>
        </FieldValue>
      </FieldWrapper>
    )
  }

  function renderContent() {
    if (!purchaseData) {
      return <p>{t('No data available')}</p>
    }

    const data = purchaseData as FeedPurchaseShowType

    return (
      <>
        <SectionTitle as='h3'>{t('Company Information')}</SectionTitle>
        <FieldsGrid>
          {renderField('building', t('Company Name'), data.companyName)}
          {renderField('', t('EVS Nr.'), data.evsNumber)}
          {renderField('id card', t('AGIS ID'), data.agisId)}        </FieldsGrid>

        <SectionTitle as='h3'>{t('Purchase Information')}</SectionTitle>
        <FieldsGrid>
          {renderField('calendar', t('Purchase date'), formatDate(data.purchaseDate))}
          {renderField('truck', t('Feed-Supplier'), data.feedSupplierName)}
          {renderField('box', t('Feed Products'), data.feedProductName || t('Customer mix'))}
        </FieldsGrid>

        <SectionTitle as='h3'>{t('Feeding Period')}</SectionTitle>
        <FieldsGrid>
          {renderField('calendar alternate', t('Feeding from'), formatDate(data.feedingFrom))}
          {renderField('calendar alternate outline', t('Feeding to'), formatDate(data.feedingTo))}
        </FieldsGrid>

        <SectionTitle as='h3'>{t('Feed Details')}</SectionTitle>
        <FieldsGridAnimalGroup>
          {renderField('balance scale', t('Purchased feed amount'), formatNumber(data.purchasedFeedAmount, 0))}
          {renderField('leaf', t('Agoline content'), formatNumber(data.agolineContent, 2))}
          {renderField('chart bar', t('Agoline amount (in kg)'), formatNumber(data.agolineAmount, 2))}
          {renderField('chart bar', t('Amount of minerals fed [calculated] (in kg)'), formatNumber(data.sumAnimalGroupsFeedAmount, 2))}
        </FieldsGridAnimalGroup>

        {data?.dairyCowsNumber && <>
          <SectionTitle as='h3'>{t('Lactating Cows')}</SectionTitle>
          <FieldsGrid>
            {renderField('paw', t('Average - Number of lactating cows fed with Agolin'), data.dairyCowsNumber)}
            {renderField('leaf', t('Daily mineral intake per cow (in g)'), formatNumber(data.dairyCowsDailyMineralIntakePerCow, 2))}
            {renderField('chart bar', t('This corresponds to g Agolin per lactating cow per day'), formatNumber(data.dairyCowsDailyAgolineAmountPerCow, 2))}
          </FieldsGrid>
        </>}
        {data?.dryCowsNumber && <><SectionTitle as='h3'>{t('Dry Cows')}</SectionTitle>
          <FieldsGrid>
            {renderField('paw', t('Average - Number of dry cows fed with Agolin'), data.dryCowsNumber)}
            {renderField('leaf', t('Daily mineral intake per cow (in g)'), formatNumber(data.dryCowsDailyMineralIntakePerCow, 2))}
            {renderField('chart bar', t('This corresponds to g Agolin per dry cow per day'), formatNumber(data.dryCowsDailyAgolineAmountPerCow, 2))}
          </FieldsGrid>
        </>}

        <SectionTitle as='h3'>{t('Status')}</SectionTitle>
        <FieldsGrid>
          {renderField('info circle', t('Status'), data.statusLabel)}
          {renderField('calendar', t('Year'), data.year)}
        </FieldsGrid>

        {(data.purchaseReceiptOriginalFilename || data.purchaseReceiptFilename) && (
          <>
            <SectionTitle as='h3'>{t('Purchase Receipt')}</SectionTitle>
            <FieldsGrid>
              {renderDownloadField('download', t('File'), data.purchaseReceiptOriginalFilename || data.purchaseReceiptFilename)}
            </FieldsGrid>
          </>
        )}

        <SectionTitle as='h3'>{t('Timestamps')}</SectionTitle>
        <FieldsGrid>
          {renderField('clock', t('Created At'), formatDate(data.createdAt))}
          {renderField('clock outline', t('Updated At'), formatDate(data.updatedAt))}
        </FieldsGrid>
      </>
    )
  }

  return (
    <Dialog
      type='dialog'
      size='large'
      style={{ minWidth: '80%' }}
      textHeader={t('Purchase Details')}
      isDialogOpen={Boolean(purchaseId)}
      isLoading={isLoadingPurchaseData || isFetchingPurchaseData}
      textClose={t('Close')}
      handleClose={onClose}
      textContent={renderContent()}
    />
  )
}

export default FeedPurchaseViewModal
